import React from "react";

// Positioning note: this copy sells the need, not the feature list. The reader's
// problem is that they finish the news knowing WHAT happened but not WHY it
// matters; every beat below points back at that.
//
// Deliberately absent:
// - any claim that the feed is finite or has an end (the app has neither)
// - any specific free-tier limit, and any price. The offer is "unlimited, free,
//   for a short while, tell us what's wrong with it" — an invitation, not a
//   transaction. Adding numbers back turns the promise act into a pricing page.
export const COPY = {
  en: {
    nav: { manifesto: "Why", waitlist: "Waitlist" },
    analytics: {
      title: "Your privacy",
      body: "Help us improve BriefBref with anonymous audience analytics. Google Analytics only loads if you allow it, and advertising features stay off.",
      allow: "Allow analytics",
      deny: "No thanks",
    },
    hero: {
      kicker: "English and French",
      headline: ["You've read the news.", "Do you know why it matters?"],
      // Rendered in the accent red inside the headline. Must appear verbatim
      // in one of the lines above or it is silently ignored.
      emphasis: "why",
      subhead: "We read the whole article. You get the why, in seconds.",
      scroll: "Scroll",
    },
    noise: {
      label: "Act 02 — Noise",
      labelSignal: "Act 02 — Signal",
      s1: "You finished scrolling. You still felt behind.",
      s1Em: "behind",
      s2: "You know what happened. You just don't know what it means.",
      s2Em: "means",
      s3a: "So we started reading ",
      s3b: "for you",
      s3c: ".",
    },
    product: {
      kicker: "Act 03 — How it works",
      title: "News that tells you why.",
      features: [
        {
          h: "We read. Then we tell you why.",
          em: "why",
          b: "Every brief explains why the story is in your feed — not just what happened. That's the part everyone else leaves out.",
        },
        {
          h: "In the language you think in.",
          em: "think",
          b: "The same brief in English and in French. Not a translation — both written properly.",
        },
        {
          h: "Chronological and local.",
          em: "local",
          b: "What happened today where you live, in the order it happened. No algorithm. No rage bait.",
        },
      ],
    },
    promise: {
      kicker: "Act 04 — The promise",
      headline: ["No ads.", "No tracking.", "No dark patterns."],
      // One word per line, alternating so the eye moves down the stack
      // instead of reading three identical red "No"s as a single block.
      headlineEm: ["No", "tracking", "No"],
      tagline: "The cleanest interface in news. That's the core promise.",
      priceLead: "Unlimited. Free. For now.",
      priceBody:
        "We're not going to ask you to take our word for it. Every brief, no limits, no cost — for a short while. This only makes sense once you've felt it.",
      priceNote:
        "Pro pricing comes later. Right now we'd rather you used it and told us what we got wrong.",
    },
    footer: {
      kicker: "Reserved for early believers",
      // Two display lines. The turn is fast/don't, so those carry the red.
      signoffLines: ["The internet moves fast.", "Good communities don't."],
      signoffEm: ["fast", "don't"],
      unlockLead: "Join before launch:",
      unlockItems: ["Founder pricing, locked in", "Early access", "A place in chapter one"],
      // Quiet credibility under the form — the close carries the big type now.
      origin: "Made in Montreal, by one person who got tired of the noise.",
      originEm: "Montreal",
      waitlistLabel: "Your email",
      placeholder: "you@example.ca",
      join: "Join the Inner Circle",
      submitting: "Submitting…",
      helper: "No spam, ever. We'll just ask what you think.",
      success: "You're in.",
      successSub: "We'll write once, when it's ready. Then we'll want your honest opinion.",
      invalid: "Please enter a valid email.",
      error: "Something went wrong. Try again.",
      legal: { copyright: "© BriefBref", privacy: "Privacy", terms: "Terms", analytics: "Analytics choices", contact: "Contact" },
      count: (n) => `${n.toLocaleString("en-CA")} ${n === 1 ? "person" : "people"} waiting`,
      badgeAppTop: "Download on the",
      badgeAppBottom: "App Store",
      badgePlayTop: "Get it on",
      badgePlayBottom: "Google Play",
    },
    hand: {
      heroL: "why",
      heroR: "not just what",
      noiseL: "wow",
      noiseR: "too loud",
      prodL: "finally",
      prodR: "en / fr",
      promiseL: "for real",
      promiseR: "yes please",
      priceL: "wait, free?",
      priceR: "no catch",
      foot: "breathe",
    },
  },
  fr: {
    nav: { manifesto: "Pourquoi", waitlist: "Liste d'attente" },
    analytics: {
      title: "Votre vie privée",
      body: "Aidez-nous à améliorer BriefBref grâce à des statistiques d’audience anonymes. Google Analytics ne se charge qu’avec votre accord, et les fonctions publicitaires restent désactivées.",
      allow: "Autoriser l’analyse",
      deny: "Non merci",
    },
    hero: {
      kicker: "En français et en anglais",
      headline: ["Vous avez lu les nouvelles.", "Savez-vous pourquoi ça compte ?"],
      emphasis: "pourquoi",
      subhead: "On lit l'article au complet. Vous obtenez le pourquoi, en quelques secondes.",
      scroll: "Défiler",
    },
    noise: {
      label: "Acte 02 — Bruit",
      labelSignal: "Acte 02 — Signal",
      s1: "Vous avez fini de défiler. Vous étiez encore dépassé.",
      s1Em: "dépassé",
      s2: "Vous savez ce qui s'est passé. Vous ne savez pas ce que ça veut dire.",
      s2Em: "veut dire",
      s3a: "Alors on s'est mis à lire ",
      s3b: "pour vous",
      s3c: ".",
    },
    product: {
      kicker: "Acte 03 — Comment ça marche",
      title: "Des nouvelles qui vous disent pourquoi.",
      features: [
        {
          h: "On lit. Ensuite on vous dit pourquoi.",
          em: "pourquoi",
          b: "Chaque brève explique pourquoi la nouvelle est dans votre fil — pas seulement ce qui s'est passé. C'est la partie que tout le monde omet.",
        },
        {
          h: "Dans la langue où vous pensez.",
          em: "pensez",
          b: "La même brève en français et en anglais. Pas une traduction — les deux écrites comme il faut.",
        },
        {
          h: "Chronologique et local.",
          em: "local",
          b: "Ce qui s'est passé aujourd'hui là où vous vivez, dans l'ordre. Aucun algorithme. Aucun piège à clics.",
        },
      ],
    },
    promise: {
      kicker: "Acte 04 — La promesse",
      headline: ["Aucune pub.", "Aucun pistage.", "Aucune manipulation."],
      headlineEm: ["Aucune", "pistage", "Aucune"],
      tagline: "L'interface la plus épurée en information. C'est notre promesse.",
      priceLead: "Illimité. Gratuit. Pour l'instant.",
      priceBody:
        "On ne vous demandera pas de nous croire sur parole. Toutes les brèves, sans limite, sans frais — pour un temps. Ça ne se comprend qu'une fois vécu.",
      priceNote:
        "L'offre pro viendra plus tard. Pour l'instant, on préfère que vous l'essayiez et que vous nous disiez ce qui cloche.",
    },
    footer: {
      kicker: "Réservé aux premiers convaincus",
      signoffLines: ["Le web va vite.", "Les bonnes communautés, non."],
      signoffEm: ["vite", "non"],
      unlockLead: "Avant le lancement :",
      unlockItems: ["Tarif fondateur, à vie", "Accès anticipé", "Une place au premier chapitre"],
      origin: "Fait à Montréal, par une personne fatiguée du bruit.",
      originEm: "Montréal",
      waitlistLabel: "Votre courriel",
      placeholder: "vous@exemple.ca",
      join: "Rejoindre le cercle",
      submitting: "Envoi…",
      helper: "Jamais de spam. On vous demandera simplement votre avis.",
      success: "Vous êtes des nôtres.",
      successSub: "On écrira une seule fois, quand ce sera prêt. Ensuite, on voudra votre avis franc.",
      invalid: "Veuillez entrer un courriel valide.",
      error: "Une erreur est survenue. Réessayez.",
      legal: { copyright: "© BriefBref", privacy: "Confidentialité", terms: "Conditions", analytics: "Choix d’analyse", contact: "Nous joindre" },
      count: (n) => `${n.toLocaleString("fr-CA")} ${n === 1 ? "personne" : "personnes"} en attente`,
      badgeAppTop: "Télécharger sur",
      badgeAppBottom: "App Store",
      badgePlayTop: "Disponible sur",
      badgePlayBottom: "Google Play",
    },
    hand: {
      heroL: "pourquoi",
      heroR: "pas juste quoi",
      noiseL: "oh la",
      noiseR: "trop fort",
      prodL: "enfin",
      prodR: "en / fr",
      promiseL: "vraiment ?",
      promiseR: "oui merci",
      priceL: "gratuit ?",
      priceR: "sans piège",
      foot: "respire",
    },
  },
};

export const LangContext = React.createContext({
  lang: "en",
  setLang: () => {},
  t: COPY.en,
});

export const useLang = () => React.useContext(LangContext);
